/**
 * COST ESTIMATOR — a real-world ballpark price for a built tower, in USD.
 *
 * Purpose: turn a `BuiltTower` (steel members, guys, foundations, mounted
 * appurtenances) into a defensible order-of-magnitude cost so the game can add a
 * BUDGET dimension to challenges. This is a *quantity-surveyor ballpark*, not a
 * bid — accuracy of ±30–50% is the goal, the same band a planner uses for
 * feasibility. Every unit rate below is commented with its basis.
 *
 * All inputs are SI (metres, kilograms, m³); all outputs are USD. Deterministic:
 * the same tower always yields the same number (no randomness, no dates).
 *
 * Unit-cost sources / assumptions (2023–2024 US telecom-tower ballparks; ranges
 * from RSMeans-style structural steel data, ANSI/TIA tower-build cost studies,
 * and published telecom construction cost guides):
 *
 *  - STRUCTURAL STEEL (galvanized, supply + shop fabrication, tower-grade
 *    lattice/angle or tube): ~$2,500–4,000 per tonne installed-to-yard.
 *    Hot-dip galvanizing + connection fab is what pushes tower steel well above
 *    raw mill price (~$1,000/t). We take $3,200/t as the mid galvanized+fab rate.
 *    (Splitting the requested "steel" vs "fabrication" lines: ~55% material,
 *    ~45% fab/galv of that combined rate — see STEEL_MATERIAL/ STEEL_FAB below.)
 *
 *  - GUY STRAND (EHS 1×7 galvanized) + hardware (thimbles, clips, turnbuckles,
 *    grips): ~$3–8 per metre of guy run. Bigger masts use bigger strand; we take
 *    $5.5/m mid. Priced separately from structural steel (per the spec) so the
 *    guy mass is *removed* from the steel line and charged here.
 *
 *  - FOUNDATION CONCRETE (ready-mix placed, incl. excavation, forming, rebar,
 *    backfill): ~$150–250 per m³ in place for small pads/piers; deep/reinforced
 *    work runs higher. We take $200/m³. Counted per leg for the base footing
 *    (each leg of a self-supporting tower sits on its own footing) and once per
 *    guy-anchor block.
 *
 *  - EQUIPMENT (appurtenances) — rough per-unit supply cost by kind, telecom
 *    ballparks (antenna/dish/radio list prices, trade level):
 *      panel sector ~$1,200, small panel ~$500, microwave dish scales with size
 *      (~$1,500 at 0.6 m up to ~$12,000 at 3 m), omni ~$400, RRU radio ~$4,000,
 *      coax/feeder ~$25 per metre of run (cable + connectors + hoisting grips).
 *    These are HARDWARE only; install labour is in erection.
 *
 *  - ERECTION / LABOUR — crane + rigging crew to stand and plumb the tower, hang
 *    steel and appurtenances. Conventionally a large fraction of the hard cost;
 *    we take ~30% of (steel + foundation) as a base, scaled up with height
 *    (taller ⇒ bigger crane, more crew-days): +1% per metre over 30 m, capped so
 *    a very tall mast lands around 30–45% of (steel + foundation).
 */
import { type BuiltTower } from "@/game/towerSpec";
import {
  concreteVolume,
  CONCRETE_GRADES,
  DEFAULT_CONCRETE,
  type ConcreteGradeKey,
} from "@/physics/foundation";
import { loadUnits, type MountedLoad, type AppurtenanceKind } from "@/game/appurtenances";

/** A priced-out cost breakdown for a tower, USD. `lines` is a human readout. */
export interface CostBreakdown {
  steel: number; // structural steel material (legs + braces, minus guys)
  fabrication: number; // shop fabrication + galvanizing of that steel
  foundation: number; // concrete footings + guy-anchor blocks (placed)
  guys: number; // EHS guy strand + hardware
  equipment: number; // mounted appurtenances (hardware supply)
  erection: number; // crane + crew to stand & rig
  total: number; // sum of the above
  lines: Array<{ label: string; usd: number }>;
}

// --- unit rates (USD) — see the file header for sources -------------------

/** Galvanized structural steel, supply + fabrication, per tonne (mid of $2.5–4k). */
const STEEL_RATE_PER_TONNE = 3200;
/** Split of the combined galvanized+fab rate: material fraction vs fab/galv. */
const STEEL_MATERIAL_FRACTION = 0.55; // ~55% material, ~45% fab/galv

/**
 * Per-MATERIAL combined supply rate (USD per tonne, installed-to-yard) and the
 * fabrication/finishing fraction of that rate. Keyed by `Material.name` (unique
 * across MATERIALS). This is what makes material choice an economic lever: a
 * titanium or carbon-fibre tower costs an order of magnitude more per tonne than
 * mild steel, while timber/bamboo/willow are far cheaper (and spaghetti is a
 * novelty-priced joke). Rates are order-of-magnitude trade ballparks; the point
 * is the *relative* tradeoff, not a bid. Falls back to steel for unknown names.
 *
 *  perTonne — combined material + fab/finish, USD/t installed.
 *  fab      — fraction of perTonne that is fabrication/finishing (rest is material).
 */
const MATERIAL_COST: Record<string, { perTonne: number; fab: number }> = {
  // structural steels — galvanized tower steel (the $3,200/t baseline, 45% fab)
  "A36": { perTonne: 3100, fab: 0.45 },
  "A572 Gr50": { perTonne: 3200, fab: 0.45 },
  "A992": { perTonne: 3300, fab: 0.45 },
  // guy strand / cable materials (priced by mass here; hardware added per-metre)
  "EHS strand": { perTonne: 2600, fab: 0.2 }, // galvanized stranding
  "Kevlar (aramid)": { perTonne: 35000, fab: 0.25 }, // aramid rope — expensive, very light
  // other metals
  "Aluminium 6061-T6": { perTonne: 6500, fab: 0.4 },
  "Stainless 304": { perTonne: 9000, fab: 0.4 },
  "Titanium Ti-6Al-4V": { perTonne: 60000, fab: 0.5 }, // aerospace alloy — brutal
  "Wrought iron": { perTonne: 5000, fab: 0.4 }, // specialty / heritage
  "Cast iron (grey)": { perTonne: 2500, fab: 0.35 }, // cast, less shop fab
  // composites
  "Carbon fibre (CFRP)": { perTonne: 55000, fab: 0.5 }, // layup labour heavy
  "Fibreglass (GFRP)": { perTonne: 9000, fab: 0.5 },
  // natural
  "Timber (Douglas-fir)": { perTonne: 900, fab: 0.25 },
  "Oak (white)": { perTonne: 2000, fab: 0.3 },
  "Bamboo (Moso)": { perTonne: 700, fab: 0.3 }, // nature's rebar, cheap
  // novelty
  "Dry spaghetti 🍝": { perTonne: 2500, fab: 0.2 }, // ~$2.5/kg retail pasta
  "Green willow 🌿": { perTonne: 400, fab: 0.2 }, // basically free sticks
  "Ice ❄️": { perTonne: 200, fab: 0.1 }, // freeze it in place
  "Glass (soda-lime)": { perTonne: 2500, fab: 0.5 },
};

/** Combined per-tonne rate + fab fraction for a material (falls back to steel). */
function materialRate(name: string): { perTonne: number; fab: number } {
  return MATERIAL_COST[name] ?? { perTonne: STEEL_RATE_PER_TONNE, fab: 1 - STEEL_MATERIAL_FRACTION };
}

/** Guy termination hardware (thimbles, clips, turnbuckles, grips), per metre — flat,
 *  size-independent. The strand itself is priced by mass × its material rate. */
const GUY_HARDWARE_PER_M = 3.0;

/** Placed foundation concrete incl. excavation/rebar/backfill, per m³ ($150–250). */
const CONCRETE_RATE_PER_M3 = 200;

/**
 * Concrete unit price (USD/m³) scaled by grade. Placement/excavation/backfill are
 * grade-independent, but the mix + rebar grade rise with strength, so a C40 pour
 * costs ~20% more per m³ than the C20 baseline. Referenced to C20 (20 MPa).
 */
function concreteRate(grade: ConcreteGradeKey): number {
  const fc = CONCRETE_GRADES[grade].fc;
  const c20 = CONCRETE_GRADES.c20.fc;
  return CONCRETE_RATE_PER_M3 * (0.8 + 0.2 * (fc / c20)); // C20→1.0×, C40→1.2×
}

/** Erection base as a fraction of (steel+foundation), before the height scale. */
const ERECTION_BASE_FRACTION = 0.3;
/** Extra erection fraction per metre of height above this datum. */
const ERECTION_HEIGHT_DATUM_M = 30;
const ERECTION_HEIGHT_SCALE_PER_M = 0.01; // +1% of base cost per m over datum
/** Cap the height uplift so erection stays in the ~30–45% band. */
const ERECTION_MAX_UPLIFT_FRACTION = 0.5;

/**
 * Per-unit equipment hardware cost (USD). For `coax` this is per METRE of run
 * (matched to `loadUnits`, which returns run length for coax and 1 otherwise).
 * Dishes are further scaled by reflector diameter in `equipmentCost`.
 */
const EQUIPMENT_RATE: Record<AppurtenanceKind, number> = {
  panel: 1000, // panel/sector antenna, trade ballpark (small ~$500, sector ~$1,200)
  dish: 3000, // BASE for a ~1 m dish; scaled by diameter below
  omni: 400, // omni / whip antenna
  rru: 4000, // remote radio unit (Ericsson/Nokia class)
  coax: 25, // per metre of feeder run (cable + connectors + grips)
};

// --- helpers ---------------------------------------------------------------

/** Price one mounted load's hardware (USD). Dishes scale ~with diameter. */
function equipmentCost(load: MountedLoad): number {
  const kind = load.appurtenance.kind;
  const units = loadUnits(load); // metres for coax, else 1
  if (kind === "dish") {
    // Dish list prices climb steeply with size; scale the ~1 m base by diameter
    // (≈ linear in D is a fair trade-level fit: 0.6 m ~$1.8k, 1.8 m ~$5.4k,
    // 3.0 m ~$9k), with a sensible floor for tiny dishes.
    const d = load.appurtenance.diameter ?? 1;
    return EQUIPMENT_RATE.dish * Math.max(0.5, d);
  }
  return EQUIPMENT_RATE[kind] * units;
}

// --- estimator -------------------------------------------------------------

/**
 * Estimate the ballpark build cost of a tower (USD). Deterministic and pure:
 * reads member steel from `towerMetrics`, guy runs/mass off the tension-only
 * members, concrete from the resolved foundation, and equipment off the mounted
 * appurtenances. See the file header for every unit rate and its basis.
 */
export function estimateCost(built: BuiltTower): CostBreakdown {
  // --- guys: total run length (m), and strand cost priced by the guy MATERIAL's
  // mass rate (thicker / exotic strand costs more) plus flat termination hardware
  // per metre. Guy mass is kept OUT of the structural-steel line below.
  let guyLengthM = 0;
  let guyStrand = 0;
  const guyMatCount = new Map<string, number>();
  for (const m of built.structure.members) {
    if (!m.tensionOnly) continue;
    const massKg = m.section.A * m.L0 * m.material.rho;
    guyLengthM += m.L0;
    guyStrand += (massKg / 1000) * materialRate(m.material.name).perTonne;
    guyMatCount.set(m.material.name, (guyMatCount.get(m.material.name) ?? 0) + 1);
  }
  const guys = guyStrand + guyLengthM * GUY_HARDWARE_PER_M;

  // --- structural (legs + braces): price EACH member by its own material rate,
  // so a titanium / carbon / bamboo tower costs what its material actually costs.
  // Split into a material line and a fabrication/finish line per that material.
  let steel = 0; // structural material (all materials, not just steel)
  let fabrication = 0; // fabrication + finishing (galv/layup/…)
  let structuralMassKg = 0;
  const structMatCount = new Map<string, number>();
  for (const m of built.structure.members) {
    if (m.tensionOnly) continue; // guys handled above
    const massKg = m.section.A * m.L0 * m.material.rho;
    structuralMassKg += massKg;
    const rate = materialRate(m.material.name);
    const combined = (massKg / 1000) * rate.perTonne;
    fabrication += combined * rate.fab;
    steel += combined * (1 - rate.fab);
    structMatCount.set(m.material.name, (structMatCount.get(m.material.name) ?? 0) + 1);
  }
  const structuralTonnes = structuralMassKg / 1000;
  const steelCombined = steel + fabrication; // combined structural cost (for erection)
  // dominant structural material name, for the readout line label
  let domMat = "steel";
  let domN = -1;
  for (const [name, n] of structMatCount) if (n > domN) ((domN = n), (domMat = name));

  // --- foundation: base footing per leg + one anchor block per guy-anchor node.
  // Each base-row leg sits on its own footing (self-supporting towers); a guyed
  // mast's few base legs likewise each get the base footing volume. Guy anchors
  // each get an anchor block (default deadman when the design gives none).
  const baseFootings = built.render.baseNodes.length;
  const baseVol = concreteVolume(built.foundation.base) * baseFootings;
  const baseGrade: ConcreteGradeKey = built.foundation.base.concrete;
  const baseFoundation = baseVol * concreteRate(baseGrade); // priced at the base grade

  const anchorCount = built.render.anchors.length;
  let anchorVol = 0;
  let anchorFoundation = 0;
  if (anchorCount > 0) {
    // The compiler falls back to a default deadman block when no anchor spec is
    // given; price whichever is resolved on the tower, else that default volume.
    const anchorSpec = built.foundation.anchor;
    // Default deadman used by the compiler: 1.5 m block × 2.0 m deep = 4.5 m³.
    const DEFAULT_ANCHOR_VOL_M3 = 1.5 * 1.5 * 2.0;
    const perAnchor = anchorSpec ? concreteVolume(anchorSpec) : DEFAULT_ANCHOR_VOL_M3;
    anchorVol = perAnchor * anchorCount;
    const anchorGrade: ConcreteGradeKey = anchorSpec?.concrete ?? DEFAULT_CONCRETE;
    anchorFoundation = anchorVol * concreteRate(anchorGrade);
  }
  const concreteVolM3 = baseVol + anchorVol;
  const foundation = baseFoundation + anchorFoundation; // grade-priced concrete

  // --- equipment: sum mounted appurtenances by kind.
  let equipment = 0;
  for (const node of built.structure.nodes) {
    if (!node.mountedLoads) continue;
    for (const { load } of node.mountedLoads) equipment += equipmentCost(load);
  }

  // --- erection: crane + crew, a fraction of (steel + foundation), scaled with
  // height (taller ⇒ bigger crane, more crew-days).
  const hardBase = steelCombined + foundation;
  const heightUplift = Math.min(
    ERECTION_MAX_UPLIFT_FRACTION,
    Math.max(0, built.height - ERECTION_HEIGHT_DATUM_M) * ERECTION_HEIGHT_SCALE_PER_M,
  );
  const erection = hardBase * (ERECTION_BASE_FRACTION + heightUplift);

  const total = steel + fabrication + foundation + guys + equipment + erection;

  const mixed = structMatCount.size > 1;
  const matLabel = mixed ? `${domMat} + ${structMatCount.size - 1} more` : domMat;
  const gradeLabel = CONCRETE_GRADES[baseGrade].name.replace(/\s*\(.*\)/, ""); // e.g. "40 MPa"
  const lines: Array<{ label: string; usd: number }> = [
    { label: `Structural ${matLabel} (${structuralTonnes.toFixed(2)} t)`, usd: steel },
    { label: "Fabrication + finishing", usd: fabrication },
    { label: `Foundation concrete (${concreteVolM3.toFixed(1)} m³ · ${gradeLabel})`, usd: foundation },
  ];
  if (guys > 0) {
    const gm = [...guyMatCount.keys()][0] ?? "strand";
    lines.push({ label: `Guys (${guyLengthM.toFixed(0)} m ${gm})`, usd: guys });
  }
  if (equipment > 0) lines.push({ label: "Equipment (appurtenances)", usd: equipment });
  lines.push({ label: "Erection / labour (crane + crew)", usd: erection });

  return { steel, fabrication, foundation, guys, equipment, erection, total, lines };
}

// --- lease economics / payback --------------------------------------------

/**
 * Monthly LEASE REVENUE per mounted appurtenance (USD/month). A tower owner rents
 * space to carriers/ISPs; a full carrier tenancy (a few sector panels + RRUs) runs
 * ~$1–3k/month in the US, so per-item rates land in the hundreds. Coax/feeder is
 * part of a tenancy, not billed on its own. This is the income that pays back the
 * build cost — more equipment = more rent, but also more wind load.
 */
const LEASE_RATE: Record<AppurtenanceKind, number> = {
  panel: 350, // sector antenna (3–6 per carrier → ~$1–2k/carrier)
  dish: 250, // microwave backhaul link (scaled by dish size below)
  omni: 200, // omni / whip
  rru: 120, // remote radio unit
  coax: 0, // feeder run — bundled into the tenancy, no standalone rent
};

/** Monthly rent for one mounted load (USD/month). Dishes scale with diameter. */
function leaseRevenue(load: MountedLoad): number {
  const kind = load.appurtenance.kind;
  if (kind === "dish") {
    const d = load.appurtenance.diameter ?? 1; // bigger link = higher-value lease
    return LEASE_RATE.dish * Math.max(0.6, d);
  }
  return LEASE_RATE[kind] * loadUnits(load);
}

export interface TowerEconomics {
  capitalCost: number; // upfront build cost (USD) — what must be paid back
  monthlyRevenue: number; // lease income from mounted equipment (USD/month)
  /** capitalCost / monthlyRevenue; Infinity when there's no equipment/revenue. */
  paybackMonths: number;
  equipmentCount: number;
}

/**
 * The payback mechanic: the tower costs money to build; the equipment on it earns
 * monthly rent. Goal = pay the build cost back as fast as possible (shortest
 * payback) — which pushes you to load MORE equipment, at the cost of more wind
 * area and a higher chance of falling over.
 */
export function towerEconomics(built: BuiltTower): TowerEconomics {
  const capitalCost = estimateCost(built).total;
  const H = Math.max(1, built.height);
  let monthlyRevenue = 0;
  let equipmentCount = 0;
  for (const node of built.structure.nodes) {
    if (!node.mountedLoads) continue;
    for (const { load } of node.mountedLoads) {
      // Higher = more valuable: better line-of-sight / coverage means higher rent.
      // Scale with mount height: ≈0.5× down low → ≈1.5× at the very top.
      const heightFactor = Math.max(0.4, Math.min(1.6, 0.5 + load.height / H));
      monthlyRevenue += leaseRevenue(load) * heightFactor;
      equipmentCount++;
    }
  }
  const paybackMonths = monthlyRevenue > 0 ? capitalCost / monthlyRevenue : Infinity;
  return { capitalCost, monthlyRevenue, paybackMonths, equipmentCount };
}

/**
 * Per-equipment monthly-rent breakdown (USD/month), one line per mounted load,
 * with the height factor folded in (matches towerEconomics). Sorted high→low so
 * the biggest earners head the tooltip. For the HUD hover-breakdown on rent.
 */
export function leaseBreakdown(built: BuiltTower): Array<{ label: string; usd: number }> {
  const H = Math.max(1, built.height);
  const lines: Array<{ label: string; usd: number }> = [];
  for (const node of built.structure.nodes) {
    if (!node.mountedLoads) continue;
    for (const { load } of node.mountedLoads) {
      const heightFactor = Math.max(0.4, Math.min(1.6, 0.5 + load.height / H));
      const usd = leaseRevenue(load) * heightFactor;
      if (usd <= 0) continue; // coax etc. earn no standalone rent
      const kind = load.appurtenance.kind;
      const dia = load.appurtenance.diameter ? ` ${load.appurtenance.diameter.toFixed(1)} m` : "";
      lines.push({ label: `${kind}${dia} @ ${load.height.toFixed(0)} m`, usd });
    }
  }
  lines.sort((a, b) => b.usd - a.usd);
  return lines;
}
